import type { BrandInsightsPageContext } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import type { ChatSessionState, ChatState } from './interfaces';
import { chatInstanceReducer } from './state.chat-instance';
import { fileUploadsReducer } from './state.file-uploads';
import { filtersReducer } from './state.filters';
import { generateEmptyChatSessionState } from './state.initial-state';
import { inputReducer } from './state.input';
import { kolSearchesReducer } from './state.kol-search';
import { pendingAttachmentsReducer } from './state.pending-attachments';
import { queriesReducer } from './state.queries';

export function chatSessionReducer(
  parentState: ChatState.State,
  action: ChatState.Action
): ChatSessionState.State {
  const state = parentState.session;

  switch (action.type) {
    case 'new-chat-added':
    case 'chat-loaded': {
      return {
        chatInstance: action.payload.chatInstance,
        input: '',
        selectedQueryIdentifier: selectedQueryReducer(state, action),
        queries: action.payload.queries,
        filters: filtersReducer(parentState, action),
        fileUploads: action.payload.fileUploads,
        pendingFindQuotes: false,
        kolSearches: action.payload.kolSearches,
        pendingQuery: null,
        pendingHintPromptIdentifier: null,
        landingCardsVisible: state.landingCardsVisible,
        pendingAttachments: [],
      };
    }

    case 'instance-context-changed': {
      const ctx = action.payload.context;
      const resetLandingCards = ctx.type === ChatContextType.Agent && ctx.data.showLandingCardsOnNewChat;
      return generateEmptyChatSessionState({
        contextItems: parentState.contextItems,
        context: ctx,
        features: parentState.settings,
        brandIdentifier: action.payload.brandIdentifier,
        pendingQuery: null,
        landingCardsVisible: resetLandingCards,
      });
    }
    case 'new-chat-started': {
      return generateEmptyChatSessionState({
        contextItems: parentState.contextItems,
        context: action.payload.context,
        features: parentState.settings,
        brandIdentifier: action.payload.brandIdentifier,
        pendingQuery: action.payload.pendingQuery,
        landingCardsVisible: action.payload.resetLandingCards ? true : state.landingCardsVisible,
      });
    }

    case 'chat-instance-removed': {
      if (state.chatInstance.id === action.payload.chatInstance.id) {
        return generateEmptyChatSessionState({
          contextItems: parentState.contextItems,
          context: state.chatInstance.context,
          features: parentState.settings,
          brandIdentifier: action.payload.brandIdentifier,
          landingCardsVisible: state.landingCardsVisible,
        });
      } else {
        return state;
      }
    }

    case 'pending-query-reset': {
      return {
        ...state,
        pendingQuery: null,
      };
    }

    case 'query-submitted': {
      return {
        chatInstance: chatInstanceReducer(state.chatInstance, action),
        input: inputReducer(state.input, action),
        selectedQueryIdentifier: selectedQueryReducer(state, action),
        queries: queriesReducer(state.queries, action),
        filters: filtersReducer(parentState, action),
        fileUploads: fileUploadsReducer(state.fileUploads, action),
        pendingFindQuotes: pendingQuotesReducer(state, action),
        kolSearches: kolSearchesReducer(state.kolSearches, action),
        pendingQuery: state.pendingQuery,
        pendingHintPromptIdentifier: null,
        landingCardsVisible: state.landingCardsVisible,
        pendingAttachments: pendingAttachmentsReducer(state.pendingAttachments, action),
      };
    }

    case 'landing-cards-visibility-toggled': {
      return {
        ...state,
        landingCardsVisible: action.payload.visible,
      };
    }

    case 'session-loaded': {
      return {
        ...state,
        landingCardsVisible: false,
      };
    }

    default:
      return {
        chatInstance: chatInstanceReducer(state.chatInstance, action),
        input: inputReducer(state.input, action),
        selectedQueryIdentifier: selectedQueryReducer(state, action),
        queries: queriesReducer(state.queries, action),
        filters: filtersReducer(parentState, action),
        fileUploads: fileUploadsReducer(state.fileUploads, action),
        pendingFindQuotes: pendingQuotesReducer(state, action),
        kolSearches: kolSearchesReducer(state.kolSearches, action),
        pendingQuery: state.pendingQuery,
        pendingHintPromptIdentifier:
          action.type === 'input-changed'
            ? resolvePendingHintPromptIdentifier(state, action)
            : state.pendingHintPromptIdentifier,
        landingCardsVisible: state.landingCardsVisible,
        pendingAttachments: pendingAttachmentsReducer(state.pendingAttachments, action),
      };
  }
}

function resolvePendingHintPromptIdentifier(
  state: ChatSessionState.State,
  action: Extract<ChatState.Action, { type: 'input-changed' }>
): string | null {
  if (action.payload.promptHintIdentifier !== undefined) {
    return action.payload.promptHintIdentifier ?? null;
  }

  // Remirror can emit a follow-up input-changed event with the same value after a hint click.
  // Preserve the pending prompt identifier for that no-op update.
  if (action.payload.value === state.input) {
    return state.pendingHintPromptIdentifier;
  }

  return null;
}

export function resetContextSubject(
  context: BrandInsightsPageContext.Item,
  resetSubject: boolean
): BrandInsightsPageContext.Item {
  if (context.type !== ChatContextType.Agent) {
    return context;
  }

  return {
    ...context,
    data: {
      ...context.data,
      initialMessage: undefined,
      subject: resetSubject ? undefined : context.data.subject,
    },
  };
}

function pendingQuotesReducer(state: ChatSessionState.State, action: ChatState.Action): boolean {
  switch (action.type) {
    case 'pending-find-quotes/set':
      return action.payload.value;
    case 'query-submitted':
      return false;

    default:
      return state.pendingFindQuotes;
  }
}

function selectedQueryReducer(state: ChatSessionState.State, action: ChatState.Action): string {
  switch (action.type) {
    case 'new-chat-added':
    case 'chat-loaded': {
      return action.payload.chatInstance.currentQueryIdentifier;
    }
    case 'query-submitted':
    case 'query-variant-submitted': {
      return action.payload.identifier;
    }
    case 'query-errored':
    case 'query-variant-errored': {
      return action.payload.queryIdentifier;
    }
    case 'query-response-available':
    case 'query-variant-response-available': {
      return action.payload.chatInstance.currentQueryIdentifier;
    }
    case 'message-variant-selected': {
      return action.payload.queryIdentifier;
    }
    default:
      return state.selectedQueryIdentifier;
  }
}
